import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { postRequestAsync } from '../../../../helpers/apiHandler';
import '../../styles/_whatsAppModal.scss';

const WebhookUrlModal = ({ show, onHide, initialUrl, wabaId, phoneNumberId, onSubmit }) => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [callbackUrl, setCallbackUrl] = useState(initialUrl || '');
  const [verifyToken, setVerifyToken] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setCallbackUrl(initialUrl || '');
      setVerifyToken('');
      setErrors({});
    }
  }, [show, initialUrl]);

  const handleCallbackUrlChange = (e) => {
    setCallbackUrl(e.target.value);
    if (errors.callbackUrl) {
      setErrors((prev) => ({ ...prev, callbackUrl: '' }));
    }
  };

  const handleVerifyTokenChange = (e) => {
    setVerifyToken(e.target.value);
    if (errors.verifyToken) {
      setErrors((prev) => ({ ...prev, verifyToken: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Simple URL validation
    if (!callbackUrl.trim()) {
      newErrors.callbackUrl = 'Callback URL is required';
    } else if (!/^https?:\/\/.+/.test(callbackUrl)) {
      newErrors.callbackUrl = 'Callback URL must be a valid URL starting with http:// or https://';
    }

    // Verify token validation
    if (!verifyToken.trim()) {
      newErrors.verifyToken = 'Verify Token is required';
    }

    // WABA ID validation
    if (!wabaId) {
      newErrors.wabaId = 'WhatsApp Business Account ID is required';
    }

    // Phone Number ID validation
    if (!phoneNumberId) {
      newErrors.phoneNumberId = 'Phone Number ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        const response = await postRequestAsync(
          '/v2/whatsapp/update-webhook-config',
          {
            webhook_url: callbackUrl,
            verify_token: verifyToken,
            waba_id: wabaId,
            phone_number_id: phoneNumberId,
          },
          access_token.token
        );

        setIsSubmitting(false);

        if (response.success) {
          console.log(response);
          onSubmit({
            callbackUrl,
            verifyToken,
            wabaId,
            phoneNumberId,
            success: true,
            message: response.message || 'Webhook configuration updated successfully',
          });
        } else {
          setErrors({
            apiError: response.message || 'Failed to update webhook configuration',
          });
        }
      } catch (error) {
        setIsSubmitting(false);
        setErrors({
          apiError: 'An error occurred while updating the webhook configuration',
        });
      }
    }
  };

  const renderTooltip = (props) => (
    <Tooltip id="info-tooltip" {...props}>
      Refer to Documentation
    </Tooltip>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="webhook-url-modal"
      centered
      className="wa-webhook-url-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="webhook-url-modal">Endpoint Configuration</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          {errors.apiError && <div className="alert alert-danger mb-3">{errors.apiError}</div>}

          <div className="d-flex flex-column">
            <Form.Group className="wa-form-group">
              <div className="d-flex align-items-center mb-2">
                <Form.Label className="mb-0">Callback URL</Form.Label>
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip}
                >
                  <i className="fas fa-info-circle ml-2" style={{ color: '#737C8A' }} />
                </OverlayTrigger>
              </div>
              <Form.Control
                type="url"
                name="callbackUrl"
                value={callbackUrl}
                onChange={handleCallbackUrlChange}
                isInvalid={!!errors.callbackUrl}
                placeholder="Your Callback URL"
              />
              <Form.Control.Feedback type="invalid">{errors.callbackUrl}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="wa-form-group">
              <Form.Label>Verify Token</Form.Label>
              <Form.Control
                type="text"
                name="verifyToken"
                value={verifyToken}
                onChange={handleVerifyTokenChange}
                isInvalid={!!errors.verifyToken}
                placeholder="Facebook Token"
              />
              <Form.Control.Feedback type="invalid">{errors.verifyToken}</Form.Control.Feedback>
              <Form.Text className="text-muted">
                Token that Facebook will echo back to you as part of callback URL verification
              </Form.Text>
            </Form.Group>
          </div>

          {errors.wabaId && <div className="alert alert-danger mb-3">{errors.wabaId}</div>}
          {errors.phoneNumberId && (
            <div className="alert alert-danger mb-3">{errors.phoneNumberId}</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Verifying...
              </>
            ) : (
              'Verify and Save'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WebhookUrlModal;
